import type { ApiError } from "@shared/types";

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, body: ApiError | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = body?.error?.code ?? "unknown";
    this.fields = body?.error?.fields ?? {};
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

/**
 * Every call to the Worker goes through here so error handling, cookies and
 * JSON encoding stay consistent. Cookies are the auth mechanism, so
 * `credentials: "same-origin"` is non-negotiable.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = options;

  const url = new URL(path.startsWith("/api") ? path : `/api${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== "") url.searchParams.set(key, String(value));
  }

  const res = await fetch(url, {
    ...rest,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    throw new ApiRequestError(res.status, payload as ApiError | null, res.statusText);
  }
  return payload as T;
}

export const get = <T,>(path: string, query?: RequestOptions["query"]) =>
  api<T>(path, { method: "GET", query });

export const post = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: "POST", body });

export const patch = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: "PATCH", body });

export const del = <T,>(path: string) => api<T>(path, { method: "DELETE" });
